import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import type { LayoutApp, LayoutUiHide } from '@typings/layout.ts';
import FloatingActionButton from '@ui/buttons/FloatingActionButton.tsx';
import { focusRelativeToElement } from '@utils/focus.ts';

import { useCSSColors } from '../hooks/useCSSColors.ts';

const SpeedDialWrapper = styled.div`
  position: fixed;
  --speed-dial-right: min(20px, 2vw);
  inset-inline-end: var(--speed-dial-right);
  bottom: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
  z-index: 161;
`;

const ButtonOptionsWrapper = styled.div<{ $isHidden?: boolean; $isMobile?: boolean }>`
  align-items: flex-end;
  bottom: 0;
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'column')};
  flex: 0 0 auto;
  margin-inline-start: auto;
  min-height: 1px;
  padding: min(20px, 2vw) 0;
  pointer-events: all;
  inset-inline-end: 0;
  width: unset;
  /* Keep the menu within the viewport at large zoom levels */
  max-width: calc(100vw - 40px);
`;

const MobileControlsWrapper = styled.div<{ $isMobileReflow?: boolean }>`
  flex-direction: ${({ $isMobileReflow }) => ($isMobileReflow ? 'row' : 'column')};
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-wrap: ${({ $isMobileReflow }) => ($isMobileReflow ? 'wrap' : 'nowrap')};
  /* When reflowing into a row (often triggered by zoom), constrain to parent width so wrapping works */
  width: ${({ $isMobileReflow }) => ($isMobileReflow ? '100%' : 'auto')};
  max-width: 100%;
  gap: ${({ $isMobileReflow }) => ($isMobileReflow ? '12px' : '8px')};
`;

const FilteredMobileMapControlWrapper = styled.div`
  display: inline-flex;
`;

const MapControlWrapper = styled.div`
  display: inline-flex;
`;

const MapPanControlsMobileWrapper = styled.div`
  pointer-events: all;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 12px;
`;

const MobileMapControlWrapper = styled.div`
  display: inline-flex;
`;

type SpeedDialProps = {
  app: LayoutApp;
  buttonOptions: React.ReactNode[];
  feedbackZone?: React.ReactNode;
  filteredMapControls: React.ReactNode[];
  isMapVisible: boolean;
  isMobileReflow: boolean;
  mapPanControlMobile?: React.ReactElement | null;
  uiHide: LayoutUiHide;
};

const SpeedDial = ({
  app,
  isMapVisible,
  uiHide,
  isMobileReflow,
  filteredMapControls,
  buttonOptions,
  mapPanControlMobile,
}: SpeedDialProps) => {
  const colors = useCSSColors();
  const T = app.gt();
  const [openControls, setOpenControls] = useState(false); // user toggle of controls
  const speedDialContainerRef = useRef<HTMLDivElement>(null);
  const speedDialItemsRef = useRef<HTMLElement[]>([]);
  const [speedDialIndex, setSpeedDialIndex] = useState(-1);
  const speedDialOpenedByKeyboardRef = useRef(false);

  useEffect(() => {
    if (!openControls) {
      speedDialItemsRef.current = [];
      setSpeedDialIndex(-1);
      speedDialOpenedByKeyboardRef.current = false;
      return;
    }
    const container = speedDialContainerRef.current;
    if (!container) {
      return;
    }
    // Collect menu items (exclude trigger button deliberately so Arrow keys never land on close trigger)
    const menuItems = Array.from(
      container.querySelectorAll<HTMLElement>(
        '[data-speed-dial-item] button,[data-speed-dial-item] [role="button"],[data-speed-dial-item] a,[data-speed-dial-item] [tabindex]',
      ),
    ).filter(el => !el.hasAttribute('disabled'));
    speedDialItemsRef.current = menuItems;
    // Remove items from tab order; trigger remains default tabbable
    menuItems.forEach(el => {
      el.tabIndex = -1;
    });
    if (speedDialOpenedByKeyboardRef.current && menuItems.length) {
      setSpeedDialIndex(0);
      setTimeout(() => menuItems[0].focus(), 0);
      speedDialOpenedByKeyboardRef.current = false;
    } else {
      setSpeedDialIndex(-1);
    }
  }, [openControls]);

  const onSpeedDialKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!openControls) {
      return;
    }
    const items = speedDialItemsRef.current;
    const triggerBtn = speedDialContainerRef.current?.querySelector<HTMLElement>('[data-speed-dial-trigger]');
    const isMenuItemTarget = e.target instanceof HTMLElement ? items.includes(e.target) : false;
    const focusPreviousControl = () => {
      const userMenuBtn = document.querySelector<HTMLElement>('[data-testid="HeaderUserMenuButton"]');
      if (userMenuBtn) {
        userMenuBtn.focus();
        return true;
      }
      return focusRelativeToElement(triggerBtn, -1, document.getElementById('button-options-wrapper'));
    };
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (focusPreviousControl()) {
          e.preventDefault();
        }
        return;
      }
      if (isMenuItemTarget && triggerBtn) {
        e.preventDefault();
        triggerBtn.focus();
        return;
      }
      return;
    }
    if (!items.length) {
      return;
    }
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = (speedDialIndex + 1) % items.length;
        setSpeedDialIndex(next);
        items[next].focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = (speedDialIndex - 1 + items.length) % items.length;
        setSpeedDialIndex(prev);
        items[prev].focus();
        break;
      }
      case 'Home': {
        e.preventDefault();
        setSpeedDialIndex(0);
        items[0].focus();
        break;
      }
      case 'End': {
        e.preventDefault();
        const last = items.length - 1;
        setSpeedDialIndex(last);
        items[last].focus();
        break;
      }
      case 'Escape': {
        e.preventDefault();
        setOpenControls(false);
        setTimeout(() => {
          const triggerBtn = speedDialContainerRef.current?.querySelector<HTMLElement>('[data-speed-dial-trigger]');
          triggerBtn?.focus();
        }, 0);
        break;
      }
      default:
        break;
    }
  };

  const openSpeedDial = () => {
    app.bus.send('appInsights/log', {
      name: 'map/speedDialOpenClick',
    });
    setOpenControls(true);
  };

  const handleResetMapClick = () => {
    app.bus.send('appInsights/log', {
      name: 'map/resetClick',
      properties: { pageLocation: 'speed dial' },
    });
    app.bus.send('app/reset');
  };

  return (
    isMapVisible && (
      <SpeedDialWrapper
        data-mobile-map-controls
        ref={speedDialContainerRef}
        onKeyDown={onSpeedDialKeyDown}
        aria-label={T('fullscreenOnlyLayout:Map controls')}
      >
        {openControls && (
          <ButtonOptionsWrapper
            $isHidden={uiHide.controls}
            $isMobile
            id="button-options-wrapper"
            role="region"
            aria-label={T('fullscreenOnlyLayout:Map controls')}
            style={{ position: 'relative', pointerEvents: 'all' }}
          >
            <MobileControlsWrapper $isMobileReflow={isMobileReflow}>
              {filteredMapControls.map((el, index: number) => (
                <FilteredMobileMapControlWrapper key={index} data-speed-dial-item>
                  {el}
                </FilteredMobileMapControlWrapper>
              ))}
              <MapControlWrapper data-speed-dial-item>
                <FloatingActionButton
                  iconName="reset"
                  aria-label={T('userMenu:Reset Map')}
                  onClick={handleResetMapClick}
                  data-testid="ResetMapButtonMobile"
                />
              </MapControlWrapper>
              {buttonOptions.map((el, key: number) => (
                <MapControlWrapper key={key} data-speed-dial-item>
                  {el}
                </MapControlWrapper>
              ))}
            </MobileControlsWrapper>
            {mapPanControlMobile && (
              <MapPanControlsMobileWrapper data-speed-dial-item>
                <MobileMapControlWrapper key={mapPanControlMobile.key ?? 'map-pan-control-mobile'}>
                  {mapPanControlMobile}
                </MobileMapControlWrapper>
              </MapPanControlsMobileWrapper>
            )}
          </ButtonOptionsWrapper>
        )}
        <FloatingActionButton
          onClick={() => (openControls ? setOpenControls(false) : openSpeedDial())}
          aria-label={
            openControls ? T('fullscreenOnlyLayout:Hide map controls') : T('fullscreenOnlyLayout:Show map controls')
          }
          aria-controls="button-options-wrapper"
          aria-expanded={openControls}
          iconName="zoom.in"
          degreesOfIconRotation={openControls ? 45 : 0}
          data-speed-dial-trigger
          style={{
            pointerEvents: 'all',
            border: 'none',
            background: colors.widgetBackground,
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (!openControls && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              speedDialOpenedByKeyboardRef.current = true;
              openSpeedDial();
            }
            // If focus is on trigger and menu is open, allow Tab to proceed normally (to next focusable outside)
          }}
        />
      </SpeedDialWrapper>
    )
  );
};

export default SpeedDial;
